/**
 * Shared sizing and color tables for `FetchButton`'s `text` variant and for `FetchIconButton`.
 *
 * Lives beside `constants.ts` rather than inside it so the button tables stay in one place a
 * reader can scan, and so a change here does not collide with unrelated fetch-ui constants.
 */

/** The color names `FetchButton` accepts. */
export type FetchButtonColor = "primary" | "neutral" | "destructive" | "success" | "warning";

/**
 * Text colors for `variant="text"` on every color except `primary`, which follows the live
 * enterprise theme instead. Each `text` value clears WCAG AA (4.5:1) on white, and `hoverText` is
 * one step darker so a hover reads as a color shift with no background.
 *
 * The solid colors the other variants use are not reused here on purpose: success `#10b981` is
 * about 2.5:1 on white, which is fine for a filled button with white text but unreadable as text.
 */
export const TEXT_BUTTON_COLORS: Record<Exclude<FetchButtonColor, "primary">, { text: string; hoverText: string }> = {
  neutral: { text: "#374151", hoverText: "#111827" },
  destructive: { text: "#dc2626", hoverText: "#b91c1c" },
  success: { text: "#047857", hoverText: "#065f46" },
  warning: { text: "#b45309", hoverText: "#92400e" },
};

/**
 * Classes `variant="text"` adds to the button. Tight padding so the label lines up with nearby
 * text, a heavier weight so it reads as an action, and a pseudo-element that grows the hit area to
 * about 32px tall without changing the button's own box or the row's layout (the same technique as
 * the admin billing chips).
 */
export const TEXT_BUTTON_CLASS =
  "px-1 py-0.5 rounded-md font-semibold before:absolute before:-inset-x-1 before:-inset-y-1.5 before:content-['']";

/** Sizes `FetchIconButton` accepts. */
export type FetchIconButtonSize = "xs" | "sm" | "md";

/**
 * Visual box, radius and glyph size for each `FetchIconButton` size, plus the `FetchButton` size
 * whose icon slot it builds on.
 *
 * The `!` overrides are needed because `FetchButton` concatenates class names without
 * tailwind-merge: without them the base `p-*` and `rounded-xl` would win on CSS order.
 *
 * Visual boxes: xs 24px, sm 32px, md 36px. Every size gets at least a 32px hit area on a fine
 * pointer and 40px on a touch screen through {@link ICON_BUTTON_HIT_AREA_CLASS}.
 */
export const ICON_BUTTON_SIZES: Record<
  FetchIconButtonSize,
  { box: string; glyph: string; buttonSize: "xs" | "sm" | "md" }
> = {
  xs: { box: "!size-6 !p-0 !rounded-md", glyph: "[&_svg]:size-3.5", buttonSize: "xs" },
  sm: { box: "!size-8 !p-0 !rounded-lg", glyph: "[&_svg]:size-4", buttonSize: "xs" },
  md: { box: "!size-9 !p-0 !rounded-lg", glyph: "[&_svg]:size-5", buttonSize: "sm" },
};

/**
 * Grows an icon button's hit area to 32px (40px on a coarse pointer) with a centered
 * pseudo-element, so the visual box and the surrounding layout stay the same size.
 *
 * `!overflow-visible` lifts `FetchButton`'s `overflow-hidden`, which would otherwise clip the
 * pseudo-element and its hit testing. The ghost and outline variants draw no overlay, so nothing
 * else depends on that clipping.
 */
export const ICON_BUTTON_HIT_AREA_CLASS =
  "!overflow-visible before:absolute before:left-1/2 before:top-1/2 before:size-8 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] [@media(pointer:coarse)]:before:size-10";

/**
 * Keeps the keyboard focus ring inside the button so a scroll area or modal body with
 * `overflow: hidden` cannot clip it.
 *
 * `!ring-2` is needed because `FetchButton` sets `box-shadow` as an inline style (none, or its
 * pressed shadow), which beats the `focus-visible:ring-2` class and hid the ring entirely.
 */
export const ICON_BUTTON_FOCUS_CLASS = "focus-visible:!ring-2 focus-visible:ring-inset";
